import * as React from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { CommandCell } from "./CommandCell";
import { ActiveCell } from "./ActiveCell";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { DropDownCell } from "./DropDownCell";
import KendoLoader from "../KendoLoader";

export const AVAILABLE_COLUMNS = [
  { key: "Vessel",             label: "Vessel" },
  { key: "PoNo",               label: "PO Number" },
  { key: "Supplier",           label: "Supplier" },
  { key: "Qty",                label: "Qty" },
  { key: "Weight",             label: "Weight (KG)" },
  { key: "Length",             label: "Length (CM)" },
  { key: "Width",              label: "Width (CM)" },
  { key: "Height",             label: "Height (CM)" },
  { key: "ChWeight",           label: "Ch. Wt. (KG)" },
  { key: "Volume",             label: "Volume (M3)" },
  { key: "CargoType",          label: "Cargo Type" },
  { key: "Commodity",          label: "Commodity" },
  { key: "TotalValue",         label: "Total Value (Currency & Value)" },
  { key: "Warehouse",          label: "Warehouse" },
  { key: "Status",             label: "Status" },
  { key: "ReceivedDate",       label: "Received Date" },
  { key: "Age",                label: "Age (Days)" },
  { key: "CargoCustomsStatus", label: "Cargo Customs Status" },
  { key: "AwbBl",              label: "AWB/BL" },
  { key: "Ref",                label: "Remarks" },
  { key: "ShipmentType",       label: "Shipment Type" },
  { key: "Date",               label: "Date" },
];

const editField = "inEdit";

// Cells below drive more than one field, so the field name is passed explicitly
// rather than taken from the owning column.
const emitChange = (props, field, value, syntheticEvent) => {
  if (props.onChange) {
    props.onChange({
      dataIndex: 0,
      dataItem: props.dataItem,
      field,
      syntheticEvent,
      value,
    });
  }
};

const formatTime24 = (date) => {
  if (!(date instanceof Date) || isNaN(date)) return "";
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const parseTime24 = (value) => {
  if (!value || typeof value !== "string") return null;
  const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(value.trim());
  if (!match) return null;
  const time = new Date();
  time.setHours(Number(match[1]), Number(match[2]), 0, 0);
  return time;
};

const formatDay = (value) =>
  value ? new Date(value).toLocaleDateString("en-CA") : "";

const EditorLabel = ({ children }) => (
  <span className="text-[10px] uppercase tracking-wide text-slate-500">
    {children}
  </span>
);

/** Frequency + scheduled time, stacked. */
const ScheduleCell = (props) => {
  const { dataItem } = props;
  const [timeValue, setTimeValue] = React.useState(formatTime24(dataItem.time));

  React.useEffect(() => {
    setTimeValue(formatTime24(dataItem.time));
  }, [dataItem.time]);

  const onTimeChange = (e) => {
    setTimeValue(e.target.value);
    emitChange(props, "time", parseTime24(e.target.value), e);
  };

  if (!dataItem.inEdit) {
    return (
      <td>
        <div className="text-sm font-medium text-slate-800">
          {dataItem.frequency || "Daily"}
        </div>
        <div className="text-xs text-slate-500">
          {dataItem.displayTime || "No time set"}
        </div>
      </td>
    );
  }

  return (
    <td>
      <div className="flex flex-col gap-1">
        <DropDownList
          data={props.frequencyOptions || []}
          value={dataItem.frequency || "Daily"}
          onChange={(e) =>
            emitChange(props, "frequency", e.value ?? e.target?.value, e.syntheticEvent)
          }
          className="h-9"
        />
        <input
          type="time"
          step="60"
          value={timeValue}
          onChange={onTimeChange}
          className="h-9 w-full rounded-md border border-[#DBDBDB] px-2 text-sm"
        />
      </div>
    </td>
  );
};

/** Start and end date, stacked. */
const PeriodCell = (props) => {
  const { dataItem } = props;

  if (!dataItem.inEdit) {
    return (
      <td>
        <div className="text-sm text-slate-800">
          {formatDay(dataItem.startDate) || "—"}
        </div>
        <div className="text-xs text-slate-500">
          to {formatDay(dataItem.endDate) || "—"}
        </div>
      </td>
    );
  }

  const invalidRange =
    dataItem.startDate &&
    dataItem.endDate &&
    new Date(dataItem.endDate) < new Date(dataItem.startDate);

  return (
    <td>
      <div className="flex flex-col gap-1">
        <EditorLabel>Start</EditorLabel>
        <DatePicker
          value={dataItem.startDate || null}
          onChange={(e) =>
            emitChange(props, "startDate", e.value ?? e.target?.value, e.syntheticEvent)
          }
          className="h-9"
          format="yyyy-MM-dd"
        />
        <EditorLabel>End</EditorLabel>
        <DatePicker
          value={dataItem.endDate || null}
          onChange={(e) =>
            emitChange(props, "endDate", e.value ?? e.target?.value, e.syntheticEvent)
          }
          className="h-9"
          format="yyyy-MM-dd"
        />
        {invalidRange && (
          <span className="text-xs text-red-600">
            End date is before the start date.
          </span>
        )}
      </div>
    </td>
  );
};

/**
 * Shows the first few chips and rolls the rest up into a "+N more" badge that
 * expands the full list in place.
 */
const ChipList = ({ items, max, emptyText, chipClass }) => {
  const [expanded, setExpanded] = React.useState(false);

  // Collapse again if the row's contents change out from under us.
  React.useEffect(() => {
    setExpanded(false);
  }, [items.length]);

  if (items.length === 0) {
    return <span className="text-sm text-gray-400">{emptyText}</span>;
  }

  const shown = expanded ? items : items.slice(0, max);
  const hidden = items.length - shown.length;

  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((item) => (
        <span key={item} className={chipClass} title={item}>
          {item}
        </span>
      ))}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="rounded-full bg-slate-200 px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-300"
        >
          +{hidden} more
        </button>
      )}
      {expanded && items.length > max && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="rounded-full px-2 py-1 text-[11px] font-medium text-sky-700 hover:underline"
        >
          Show less
        </button>
      )}
    </div>
  );
};

const ReportColumnsCell = (props) => {
  const { dataItem } = props;
  const selected = dataItem.reportColumns || [];
  const selectedKeys = selected.map((column) => column.key);
  const [filter, setFilter] = React.useState("");

  const toggleColumn = (column, e) => {
    const next = selectedKeys.includes(column.key)
      ? selected.filter((item) => item.key !== column.key)
      : [...selected, column];
    emitChange(props, props.field, next, e?.syntheticEvent || e);
  };

  if (!dataItem.inEdit) {
    return (
      <td>
        <ChipList
          items={selected.map((c) => c.label)}
          max={3}
          emptyText="All columns"
          chipClass="rounded-full bg-sky-100 px-2 py-1 text-[11px] font-medium text-sky-800"
        />
      </td>
    );
  }

  const visible = AVAILABLE_COLUMNS.filter((column) =>
    column.label.toLowerCase().includes(filter.trim().toLowerCase())
  );

  return (
    <td>
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter columns"
            className="h-8 flex-1 rounded-md border border-[#DBDBDB] px-2 text-xs outline-none focus:border-sky-500"
          />
          <button
            type="button"
            className="text-xs font-medium text-sky-700 hover:underline"
            onClick={(e) => emitChange(props, props.field, [], e)}
          >
            Clear
          </button>
        </div>
        <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white p-2">
          {visible.map((column) => (
            <label
              key={column.key}
              className="flex items-center gap-2 py-1 text-sm"
            >
              <input
                type="checkbox"
                checked={selectedKeys.includes(column.key)}
                onChange={(e) => toggleColumn(column, e)}
                className="k-checkbox k-checkbox-md k-rounded-md"
              />
              <span className="truncate">{column.label}</span>
            </label>
          ))}
          {visible.length === 0 && (
            <div className="py-1 text-xs text-slate-400">No columns match.</div>
          )}
        </div>
        <span className="text-[11px] text-slate-500">
          {selected.length === 0
            ? "None selected — all columns are sent."
            : `${selected.length} selected`}
        </span>
      </div>
    </td>
  );
};

const parseRecipients = (value) =>
  !value
    ? []
    : value
        .split(/[;,\n]+/)
        .map((item) => item.trim())
        .filter(Boolean);

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const RecipientsCell = (props) => {
  const { dataItem } = props;
  const [inputValue, setInputValue] = React.useState("");
  const [recipientList, setRecipientList] = React.useState(() =>
    parseRecipients(dataItem.recipients)
  );
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    setRecipientList(parseRecipients(dataItem.recipients));
  }, [dataItem.recipients]);

  const notifyChange = (nextList, syntheticEvent) => {
    setRecipientList(nextList);
    emitChange(props, props.field, nextList.join(", "), syntheticEvent);
  };

  const addRecipients = (rawValue, syntheticEvent) => {
    const nextRecipients = parseRecipients(rawValue);
    if (nextRecipients.length === 0) {
      setInputValue("");
      return;
    }

    const nextList = [...recipientList];
    let invalidFound = false;
    nextRecipients.forEach((recipient) => {
      if (!nextList.includes(recipient)) nextList.push(recipient);
      if (!isValidEmail(recipient)) invalidFound = true;
    });

    setError(
      invalidFound ? "One or more recipients are not valid email addresses." : ""
    );
    notifyChange(nextList, syntheticEvent);
    setInputValue("");
  };

  const removeRecipient = (email) =>
    notifyChange(recipientList.filter((item) => item !== email), null);

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";") {
      e.preventDefault();
      addRecipients(inputValue, e);
    }
  };

  if (!dataItem.inEdit) {
    return (
      <td>
        <ChipList
          items={recipientList}
          max={2}
          emptyText="No recipients"
          chipClass="max-w-full truncate rounded-full bg-slate-200 px-2 py-1 text-[11px] text-slate-800"
        />
      </td>
    );
  }

  return (
    <td>
      <div className="flex flex-col gap-1">
        <div className="flex min-h-[3rem] flex-wrap items-center gap-1 rounded-md border border-[#DBDBDB] bg-white p-2">
          {recipientList.map((email) => (
            <span
              key={email}
              className="flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-800"
            >
              <span className="max-w-[150px] truncate">{email}</span>
              <button
                type="button"
                onClick={() => removeRecipient(email)}
                className="text-slate-400 hover:text-slate-600"
              >
                ×
              </button>
            </span>
          ))}
          <input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleInputKeyDown}
            onBlur={() => inputValue.trim() && addRecipients(inputValue, null)}
            placeholder="Add recipient and press Enter"
            className="min-w-[120px] flex-1 border-none bg-transparent p-0 text-sm outline-none"
          />
        </div>
        <div className="text-[11px] text-slate-500">
          Press Enter, comma, or semicolon to add.
        </div>
        {error && <div className="text-xs text-red-600">{error}</div>}
      </div>
    </td>
  );
};

const LastRefreshedCell = ({ dataItem }) => (
  <td>
    {dataItem.lastRefreshed ? (
      <span className="text-sm text-slate-700">{dataItem.lastRefreshed}</span>
    ) : (
      <span className="text-sm text-gray-400">Never</span>
    )}
  </td>
);

const EditableGrid = ({
  data,
  setData,
  customers,
  frequencyOptions,
  formatTime,
  editingForm,
  setEditingForm,
  loading,
  updateCustomersDropdown,
}) => {
  const remove = (dataItem) => {
    setData((pre) => {
      const modifiedData = pre.filter((x) => x.id !== dataItem.id);
      updateCustomersDropdown(modifiedData.map((x) => x.customerId));
      return modifiedData;
    });
  };

  const update = (dataItem) => {
    dataItem.inEdit = false;
    setData((pre) =>
      pre.map((x) => (x.id === dataItem.id ? { ...dataItem } : x))
    );
  };

  const discard = () => {
    if (editingForm.isNew) remove(editingForm);
    else
      setData((pre) => {
        const modifiedData = pre.map((x) =>
          x.id === editingForm.id ? { ...editingForm } : x
        );
        updateCustomersDropdown(modifiedData.map((x) => x.customerId));
        return modifiedData;
      });
  };

  const enterEdit = (dataItem) => {
    setEditingForm({ ...dataItem, isNew: false });
    setData((pre) =>
      pre.map((item) =>
        item.id === dataItem.id ? { ...item, inEdit: true } : item
      )
    );
  };

  const itemChange = (event) => {
    // `data` is the filtered/sorted view, so edits are applied to the full list.
    setData((pre) => {
      const newData = pre.map((item) =>
        item.id === event.dataItem.id
          ? event.field === "time"
            ? {
                ...item,
                time: event.value,
                displayTime: event.value
                  ? formatTime(event.value, "display")
                  : "",
              }
            : event.field === "customerId"
            ? {
                ...item,
                customerId: event.value?.id,
                customerName: event.value?.name,
              }
            : { ...item, [event.field]: event.value }
          : item
      );
      updateCustomersDropdown(newData.map((x) => x.customerId));
      return newData;
    });
  };

  const validate = (dataItem) => {
    const hasInvalidRecipient = parseRecipients(dataItem.recipients).some(
      (email) => !isValidEmail(email)
    );

    return (
      dataItem.customerId === null ||
      dataItem.time === null ||
      !dataItem.frequency ||
      dataItem.startDate === null ||
      dataItem.endDate === null ||
      hasInvalidRecipient ||
      new Date(dataItem.endDate) < new Date(dataItem.startDate)
    );
  };

  const commandCellProps = {
    edit: enterEdit,
    remove: remove,
    discard: discard,
    update: update,
    validate: validate,
    editField: editField,
  };

  const rowRender = (trElement, rowProps) => {
    const { dataItem, dataIndex } = rowProps;
    const background = dataItem.inEdit
      ? dataItem.isNew
        ? "bg-sky-50"
        : "bg-amber-50"
      : dataIndex % 2 === 1
      ? "bg-slate-50/70"
      : "bg-white";

    return React.cloneElement(
      trElement,
      {
        className: `${trElement.props.className || ""} ${background}`,
      },
      trElement.props.children
    );
  };

  return (
    <div className="relative h-full min-h-0">
      {loading && <KendoLoader />}
      <Grid
        style={{ height: "100%", width: "100%" }}
        data={data}
        onItemChange={itemChange}
        rowRender={rowRender}
        editField={editField}
      >
        <GridColumn
          field="customerId"
          title="Client"
          width="270"
          cell={(props) => <DropDownCell {...props} customers={customers} />}
        />
        <GridColumn
          field="frequency"
          title="Schedule"
          width="150"
          cell={(props) => (
            <ScheduleCell {...props} frequencyOptions={frequencyOptions} />
          )}
        />
        <GridColumn field="startDate" title="Period" width="190" cell={PeriodCell} />
        <GridColumn
          field="recipients"
          title="Recipients"
          width="280"
          cell={RecipientsCell}
        />
        <GridColumn
          field="reportColumns"
          title="Report Columns"
          width="300"
          cell={ReportColumnsCell}
        />
        <GridColumn
          field="lastRefreshed"
          title="Last Refreshed"
          editable={false}
          width="190"
          cell={LastRefreshedCell}
        />
        <GridColumn
          field="active"
          title="Status"
          editable={false}
          cell={ActiveCell}
          width="110"
        />
        <GridColumn
          title="Action"
          cell={(props) => <CommandCell {...props} {...commandCellProps} />}
          width="130"
        />
      </Grid>
    </div>
  );
};

export default EditableGrid;
